import { faChevronDown, faPen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import _ from "lodash";

import { Alert, Badge, Button, Chip, Group, Menu, Modal, Table, Text, TextInput } from "@mantine/core";
import { toast } from 'react-toastify';
import { getManageablePersons } from "../../../data/actions/personActions";
import { BasketballGrade } from "../../../types/common/basketballGrades";
import { IPerson, PersonAssociationTenure } from "../../../types/persons/person";

import { getManageableAssociations } from "../../../data/actions/associationActions";
import PersonApi from "../../../data/api/personsApi";
import { IState } from "../../../data/reducers/state";
import { IAssociation } from "../../../types/association/Association";
import { Role } from "../../../types/persons/Role";
import { Privilege } from "../../../types/persons/privilege";
import { AdminPageLayout } from "../../admin/common";
import { PersonLink } from "../../common/Links";
import { CenteredLoader } from "../../common/loader";
import {
  RoleIconDisplay,
  roleIconConfig,
} from "../../management/Shifts/ShiftEditor/roleIcons";
import PersonEditor from "./PersonEditor";
import PersonExpressForm from "./PersonExpressForm";

interface IPrivilegedUsersState {
  isLoading: boolean;
  personsMap: { [id: string]: IPerson };
  privilegedPersons?: string[];
  error?: string;
  currentAssociationId?: string;
  filters?: {
    text?: string;
    privilege?: Privilege;
    roles?: Role[];
  };
  filtered?: string[];
  privilegeFilterIsOpen?: boolean;
}

const SearchHelper = (person, keywords) => {
  const fields = [
    person.firstName,
    person.lastName,
    person.preferredName,
    person.externalIdentifier,
  ]
    .filter((k) => k && k.length > 0)
    .map((k) => k.toLowerCase());
  return keywords.every((k) => fields.some((f) => f.startsWith(k)));
};

type SortColumn = 'identifier' | 'firstName' | 'lastName';
type SortDirection = 'asc' | 'desc';
type SortState = { column: SortColumn; direction: SortDirection };

const toggleSort = (current: SortState, column: SortColumn): SortState => {
  if (current.column === column) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { column, direction: 'asc' };
};

const getSortIndicator = (sortState: SortState, column: SortColumn): string => {
  if (sortState.column !== column) return '';
  return sortState.direction === 'asc' ? ' ↑' : ' ↓';
};

// Shared table component for rendering persons lists
const PersonsTable: React.FunctionComponent<{
  personIds: string[];
  personsMap: { [id: string]: IPerson };
  associationId: string;
  editOfficial: (pid: string) => void;
  onPersonUpdated: (person: IPerson) => void;
  isLoading?: boolean;
}> = ({ personIds, personsMap, associationId, editOfficial, onPersonUpdated, isLoading }) => {
  const [sortState, setSortState] = React.useState<SortState>({ column: 'lastName', direction: 'asc' });

  const sortedPersonIds = React.useMemo(() => {
    return [...personIds].sort((a, b) => {
      const personA = personsMap[a];
      const personB = personsMap[b];
      if (!personA || !personB) return 0;

      let fieldA: string;
      let fieldB: string;

      switch (sortState.column) {
        case 'identifier':
          fieldA = (personA.externalIdentifier || '').toLowerCase();
          fieldB = (personB.externalIdentifier || '').toLowerCase();
          break;
        case 'firstName':
          fieldA = (personA.firstName || '').toLowerCase();
          fieldB = (personB.firstName || '').toLowerCase();
          break;
        case 'lastName':
        default:
          fieldA = (personA.lastName || '').toLowerCase();
          fieldB = (personB.lastName || '').toLowerCase();
          break;
      }

      const comparison = fieldA.localeCompare(fieldB);
      return sortState.direction === 'desc' ? -comparison : comparison;
    });
  }, [personIds, personsMap, sortState]);

  return (
    <>
      {isLoading && <CenteredLoader />}
      <div style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <Table
            striped
            highlightOnHover
            horizontalSpacing="md"
            verticalSpacing="md"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ width: '100px', minWidth: '100px', cursor: 'pointer' }}
                  onClick={() => setSortState(toggleSort(sortState, 'identifier'))}
                >
                  Identifier{getSortIndicator(sortState, 'identifier')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSortState(toggleSort(sortState, 'firstName'))}
                >
                  First Name{getSortIndicator(sortState, 'firstName')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSortState(toggleSort(sortState, 'lastName'))}
                >
                  Last Name{getSortIndicator(sortState, 'lastName')}
                </Table.Th>
                <Table.Th>Privilege</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Tenure</Table.Th>
                <Table.Th>Grade</Table.Th>
                <Table.Th style={{ width: '80px', minWidth: '80px' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedPersonIds.map((pid) => {
                const person = personsMap[pid];
                if (!person) {
                  return (
                    <Table.Tr key={pid}>
                      <Table.Td colSpan={8}>No Person for: {pid}</Table.Td>
                    </Table.Tr>
                  );
                }
                return (
                  <PersonRow
                    key={pid}
                    person={person}
                    editOfficial={editOfficial}
                    associationId={associationId}
                    onPersonUpdated={onPersonUpdated}
                  />
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      </div>
    </>
  );
};

interface PersonsViewerProps {
  // persons: string[]
  // personsMap: { [id: string]: IPerson }
  associations: { [id: string]: IAssociation };

  defaultAssociationId?: string;
  actions: {
    getManageablePersons: () => void;
    getManageableAssociations: () => Promise<IAssociation[]>;
  };
}

type OfficialsFilter = "all" | "permanent" | "elite" | "casual" | "temporary" | "affiliate" | "privileged";

interface IPersonsViewerState {
  isEditing: boolean;
  personEditing: null | string;

  associationId?: string;
  filter: OfficialsFilter;
  counts?: Record<OfficialsFilter, number>;
  rolesFilter: Role[];
}

interface IPersonsViewerTabProps {
  associationId?: string;
  editOfficial: (string) => void;
  rolesFilter: Role[];
}

// class PersonalProfilePage extends TabBase<IPersonalProfilePageProps, IPersonalProfilePageState, IProfileTabProps> {

class PersonsViewer extends React.PureComponent<
  PersonsViewerProps,
  IPersonsViewerState
> {
  private _isMounted = false;

  constructor(props, content) {
    super(props, content);
    const state: IPersonsViewerState = {
      isEditing: false,
      personEditing: null,
      associationId: undefined,
      filter: "all",
      counts: undefined,
      rolesFilter: [],
    };

    this.editOfficial = this.editOfficial.bind(this);
    this.stopEditing = this.stopEditing.bind(this);
    this.onSave = this.onSave.bind(this);
    this.newPersonButtonClicked = this.newPersonButtonClicked.bind(this);
    this.onAssociationChange = this.onAssociationChange.bind(this);
    this.setFilter = this.setFilter.bind(this);
    this.toggleRole = this.toggleRole.bind(this);

    if (props.defaultAssociationId) {
      state.associationId = props.defaultAssociationId;
    }

    this.state = state;

    this.props.actions.getManageablePersons();
    this.props.actions.getManageableAssociations();
  }

  componentDidMount() {
    this._isMounted = true;
    if (this.state.associationId) {
      this.loadCounts(this.state.associationId);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: PersonsViewerProps) {
    // Update association if it changes from the wrapper
    if (this.props.defaultAssociationId !== prevProps.defaultAssociationId) {
      this.setState(
        { associationId: this.props.defaultAssociationId },
        () => this.loadCounts(this.state.associationId),
      );
    }
  }

  onAssociationChange(associationId: string) {
    this.setState({ associationId }, () => this.loadCounts(associationId));
  }

  setFilter(filter: OfficialsFilter) {
    this.setState({ filter });
  }

  toggleRole(role: Role) {
    this.setState((prevState) => {
      const rolesFilter = prevState.rolesFilter.includes(role)
        ? prevState.rolesFilter.filter(r => r !== role)
        : [...prevState.rolesFilter, role];
      return { rolesFilter };
    });
  }

  private async loadCounts(associationId?: string | null) {
    if (!associationId) {
      if (this._isMounted) this.setState({ counts: undefined });
      return;
    }

    try {
      const persons = await PersonApi.getActiveAtAssociation(associationId);
      const counts: Record<OfficialsFilter, number> = {
        all: 0,
        permanent: 0,
        elite: 0,
        casual: 0,
        temporary: 0,
        affiliate: 0,
        privileged: 0,
      };

      persons.forEach((person) => {
        const association = person.associations.find(
          (assoc) =>
            assoc.associationId &&
            String(assoc.associationId) === String(associationId) &&
            !assoc.finish,
        );
        if (!association) return;

        const tenure = (association.tenure as PersonAssociationTenure) || "permanent";

        counts.all += 1;
        if (tenure === "permanent") counts.permanent += 1;
        if (tenure === "elite") counts.elite += 1;
        if (tenure === "casual") counts.casual += 1;
        if (tenure === "temporary") counts.temporary += 1;
        if (tenure === "affiliate") counts.affiliate += 1;

        if (
          typeof association.privilege === "number" &&
          Privilege.canManage(association.privilege)
        ) {
          counts.privileged += 1;
        }
      });

      if (this._isMounted) {
        this.setState({ counts });
      }
    } catch (error) {
      console.error("Failed to load official counts", error);
    }
  }

  editOfficial(personId) {
    this.setState({ isEditing: true, personEditing: personId });
  }

  newPersonButtonClicked() {
    this.setState({ isEditing: true, personEditing: null });
  }

  stopEditing() {
    this.setState((st) => ({ ...st, isEditing: false }));
  }

  onSave() {
    console.log("Person saved");
    this.stopEditing();
  }

  render() {
    const { filter } = this.state;

    const filterOptions: Array<{ value: OfficialsFilter; label: string }> = [
      { value: "all", label: "All" },
      { value: "permanent", label: "Permanent" },
      { value: "elite", label: "Elite" },
      { value: "casual", label: "Casual" },
      { value: "temporary", label: "Temporary" },
      { value: "affiliate", label: "Affiliate" },
      { value: "privileged", label: "Elevated privileges" },
    ];

    const counts = this.state.counts;

    const associationId = this.state.associationId;
    const refreshCounts = () => this.loadCounts(associationId);

    let bodyContent: React.ReactNode = null;
    switch (filter) {
      case "elite":
        bodyContent = (
          <ActiveOfficialsList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
            tenureFilters={["elite"]}
            summaryLabel="elite"
            emptyAssociationMessage="Set an association to manage to see a list of elite officials"
            onAssociationRefresh={refreshCounts}
          />
        );
        break;
      case "casual":
        bodyContent = (
          <ActiveOfficialsList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
            tenureFilters={["casual"]}
            summaryLabel="casual"
            emptyAssociationMessage="Set an association to manage to see a list of casual officials"
            onAssociationRefresh={refreshCounts}
          />
        );
        break;
      case "temporary":
        bodyContent = (
          <ActiveOfficialsList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
            tenureFilters={["temporary"]}
            summaryLabel="temporary"
            emptyAssociationMessage="Set an association to manage to see a list of temporary officials"
            onAssociationRefresh={refreshCounts}
          />
        );
        break;
      case "privileged":
        bodyContent = (
          <PrivilegedUsersList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
          />
        );
        break;
      case "permanent":
        bodyContent = (
          <ActiveOfficialsList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
            tenureFilters={["permanent"]}
            summaryLabel="permanent"
            emptyAssociationMessage="Set an association to manage to see a list of permanent officials"
            onAssociationRefresh={refreshCounts}
          />
        );
        break;
      case "affiliate":
        bodyContent = (
          <ActiveOfficialsList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
            tenureFilters={["affiliate"]}
            summaryLabel="affiliate"
            emptyAssociationMessage="Set an association to manage to see a list of affiliate officials"
            onAssociationRefresh={refreshCounts}
          />
        );
        break;
      case "all":
      default:
        bodyContent = (
          <ActiveOfficialsList
            associationId={associationId}
            editOfficial={this.editOfficial}
            rolesFilter={this.state.rolesFilter}
            summaryLabel="all"
            emptyAssociationMessage="Set an association to manage to see a list of officials"
            onAssociationRefresh={refreshCounts}
          />
        );
        break;
    }

    return (
      <>
        <AdminPageLayout
          title="People Manager"
          description="Manage officials and staff members within your association"
          icon="fas fa-users"
          actions={
            <Button
              color="blue"
              onClick={this.newPersonButtonClicked}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Official
            </Button>
          }
        >
          <Group gap="xs" mb="md">
            {filterOptions.map((option) => {
              const isActive = filter === option.value;
              return (
                <Button
                  key={option.value}
                  variant={isActive ? "filled" : "light"}
                  color="blue"
                  radius="xl"
                  onClick={() => this.setFilter(option.value)}
                >
                  <Group gap="xs" align="center">
                    <span>{option.label}</span>
                    <Badge
                      size="sm"
                      radius="xl"
                      variant={isActive ? "filled" : "outline"}
                      color={isActive ? "blue" : "gray"}
                      style={
                        isActive
                          ? {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            color: 'var(--mantine-color-white)',
                          }
                          : undefined
                      }
                    >
                      {counts ? counts[option.value] ?? 0 : "–"}
                    </Badge>
                  </Group>
                </Button>
              );
            })}
          </Group>
          <Group gap="xs" mb="md">
            {Role.getAllRoles().map(role => {
              const iconConfig = roleIconConfig.find(c => c.role === role);
              return (
                <Chip
                  key={role}
                  size="sm"
                  checked={this.state.rolesFilter.includes(role)}
                  onClick={() => this.toggleRole(role)}
                >
                  <Group gap="xs">
                    {iconConfig && <FontAwesomeIcon icon={iconConfig.icon} />}
                    {Role.toString(role)}
                  </Group>
                </Chip>
              );
            })}
          </Group>
          {bodyContent}
        </AdminPageLayout>

        <Modal
          opened={this.state.isEditing}
          onClose={this.stopEditing}
          title={this.state.personEditing ? "Person Editor" : "Express person creator"}
          size={this.state.personEditing ? "lg" : "xl"}
          style={{ paddingBottom: "10px" }}
        >
          {this.state.personEditing ? (
            <PersonEditor
              personId={this.state.personEditing}
              onSave={this.onSave}
              onCancel={this.stopEditing}
              associationIdContext={this.state.associationId}
            />
          ) : (
            <PersonExpressForm
              defaultAssociationId={
                this.state.associationId || this.props.defaultAssociationId
              }
              onCancel={this.stopEditing}
              onComplete={(_personId) => {
                this.props.actions.getManageablePersons();
                this.loadCounts(this.state.associationId);
                this.stopEditing();
              }}
              onOpenExisting={(personId) => {
                this.editOfficial(personId);
              }}
            />
          )}
        </Modal>
      </>
    );
  }
}

function mapStateToProps(state: IState, ownProps) {
  return {
    personsMap: state.persons.map,
    associations: state.associations.map,
    // Don't use auth.adminAssociation - let the wrapper provide it
    defaultAssociationId: ownProps.defaultAssociationId,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { getManageablePersons, getManageableAssociations },
      dispatch,
    ),
  };
}

interface IActiveOfficialsListState {
  currentAssociationId?: string;
  isLoading: boolean;
  error?: string;
  personsMap: { [id: string]: IPerson };

  activePersons: string[];
  filters: {
    text?: string;
    roles?: Role[];
  };
}

const ActiveOfficialsList: React.FunctionComponent<
  IPersonsViewerTabProps & {
    tenureFilters?: PersonAssociationTenure[];
    summaryLabel?: string;
    emptyAssociationMessage?: string;
    onAssociationRefresh?: () => void;
  }
> = ({
  associationId,
  editOfficial,
  rolesFilter,
  tenureFilters,
  summaryLabel,
  emptyAssociationMessage,
  onAssociationRefresh,
}) => {
    if (!associationId) {
      return (
        <p>
          <em>
            {emptyAssociationMessage ||
              "Set an association to manage to see a list of officials"}
          </em>
        </p>
      );
    }

    const [state, setState] = React.useState<IActiveOfficialsListState>({
      isLoading: false,
      personsMap: {},
      activePersons: [],
      filters: {},
    });

    const updateState = React.useCallback(
      (
        patch:
          | Partial<IActiveOfficialsListState>
          | ((prev: IActiveOfficialsListState) => Partial<IActiveOfficialsListState>),
      ) => {
        setState((prev) => {
          const partial = typeof patch === "function" ? patch(prev) : patch;
          return { ...prev, ...partial };
        });
      },
      [],
    );

    const handlePersonUpdate = React.useCallback(
      (updatedPerson: IPerson) => {
        if (!updatedPerson?._id) {
          return;
        }
        updateState((prev) => {
          const existing = prev.personsMap[updatedPerson._id];
          let mergedPerson: IPerson;
          if (existing) {
            const nextPerson: IPerson = { ...existing };
            Object.entries(updatedPerson).forEach(([key, value]) => {
              if (key === "associations") {
                return;
              }
              if (value !== undefined) {
                (nextPerson as Record<string, unknown>)[key] = value;
              }
            });
            if (
              Array.isArray(updatedPerson.associations) &&
              updatedPerson.associations.length > 0
            ) {
              nextPerson.associations = updatedPerson.associations;
            }
            mergedPerson = nextPerson;
          } else {
            mergedPerson = updatedPerson;
          }
          const nextActivePersons = existing
            ? prev.activePersons
            : [...prev.activePersons, updatedPerson._id];
          return {
            personsMap: {
              ...prev.personsMap,
              [updatedPerson._id]: mergedPerson,
            },
            activePersons: nextActivePersons,
          };
        });
        if (onAssociationRefresh) {
          onAssociationRefresh();
        }
      },
      [onAssociationRefresh, updateState],
    );

    React.useEffect(() => {
      if (!associationId) {
        updateState({
          currentAssociationId: undefined,
          personsMap: {},
          activePersons: [],
          filters: {},
          isLoading: false,
          error: undefined,
        });
        return;
      }

      let cancelled = false;
      updateState({ isLoading: true, error: undefined });

      PersonApi.getActiveAtAssociation(associationId)
        .then((persons) => {
          if (cancelled) return;
          updateState({
            currentAssociationId: associationId,
            personsMap: _.keyBy(persons, "_id"),
            activePersons: persons.map((p) => p._id),
            filters: {},
            isLoading: false,
          });
        })
        .catch((er) => {
          if (cancelled) return;
          updateState({
            isLoading: false,
            error: er.message,
          });
        });

      return () => {
        cancelled = true;
      };
    }, [associationId, updateState]);

    const filterPersons: (params: {
      text?: string | null;
      roles?: Role[];
    }) => void = React.useCallback(({ text, roles }) => {
      updateState((prev) => {
        const nextFilters = {
          text:
            text === undefined
              ? prev.filters?.text
              : text?.trim()
                ? text
                : undefined,
          roles:
            roles === undefined
              ? prev.filters?.roles
              : roles || undefined,
        };
        return { filters: nextFilters };
      });
    }, [updateState]);

    React.useEffect(() => {
      filterPersons({ roles: rolesFilter });
    }, [rolesFilter, filterPersons]);

    const targetAssociationId = associationId?.toString();

    const filteredIds = React.useMemo(() => {
      if (!targetAssociationId) return [];
      const baseIds = state.activePersons || [];

      let ids = baseIds;

      if (state.filters.roles && state.filters.roles.length > 0) {
        ids = ids.filter((pid) => {
          const person = state.personsMap[pid];
          const association = person?.associations.find(
            (a) =>
              a.associationId &&
              targetAssociationId &&
              String(a.associationId) === targetAssociationId &&
              !a.finish,
          );
          const pRole = association?.role;
          if (!pRole) return false;
          return state.filters.roles!.some(role => Role.hasRole(pRole, role));
        });
      }

      if (state.filters.text) {
        const kwds = state.filters.text.toLowerCase().split(" ").filter(Boolean);
        if (kwds.length > 0) {
          ids = ids.filter((pid) => SearchHelper(state.personsMap[pid], kwds));
        }
      }

      return ids;
    }, [state.activePersons, state.filters, state.personsMap, targetAssociationId]);

    const allowedTenures: PersonAssociationTenure[] =
      tenureFilters && tenureFilters.length > 0
        ? tenureFilters
        : ["permanent", "elite", "casual", "temporary", "affiliate"];

    const visibleIds = React.useMemo(
      () =>
        filteredIds.filter((pid) => {
          const person = state.personsMap[pid];
          if (!person || !targetAssociationId) return false;
          const association = person.associations.find(
            (a) =>
              a.associationId &&
              String(a.associationId) === targetAssociationId &&
              !a.finish,
          );
          const tenure =
            (association?.tenure as PersonAssociationTenure) || "permanent";
          return allowedTenures.includes(tenure);
        }),
      [allowedTenures, filteredIds, state.personsMap, targetAssociationId],
    );

    const descriptor = summaryLabel
      ? summaryLabel.charAt(0).toUpperCase() + summaryLabel.slice(1)
      : "Active";
    const summaryText = `${visibleIds.length} ${descriptor.toLowerCase()} officials`;

    return (
      <div>
        <div style={{ width: "100%", marginBottom: "10px" }}>
          <TextInput
            className="mr-sm-4 "
            placeholder="Search persons"
            onChange={(e) => filterPersons({ text: e.currentTarget.value })}
            style={{
              maxWidth: "100%",
              minWidth: "200px",
              width: "100%",
              marginBottom: "10px",
            }}
          />
          <Text c="dimmed" size="sm" ta="right" style={{ marginTop: "8px" }}>
            <em>{summaryText}</em>
          </Text>
        </div>
        <PersonsTable
          personIds={visibleIds}
          personsMap={state.personsMap}
          associationId={targetAssociationId!}
          editOfficial={editOfficial}
          onPersonUpdated={handlePersonUpdate}
          isLoading={state.isLoading}
        />
      </div>
    );
  };

const PersonRow: React.FunctionComponent<{
  person: IPerson;
  editOfficial: (pid: string) => void;
  associationId: string;
  onPersonUpdated: (person: IPerson) => void;
}> = ({ person, editOfficial, associationId, onPersonUpdated }) => {
  interface IPersonRowState {
    extraOptionsIsOpen?: boolean;
    isConverting?: boolean;
    isDeactivating?: boolean;
  }

  const [state, _setState] = React.useState<IPersonRowState>({});

  const setState = (newState: Partial<IPersonRowState>) =>
    _setState({ ...state, ...newState });

  const activeAssociation = person.associations.find(
    (a) => a.associationId && String(a.associationId) === associationId && !a.finish,
  );
  const currentTenure: PersonAssociationTenure = (activeAssociation?.tenure as PersonAssociationTenure) || "permanent";
  const formatTenure = (tenure: PersonAssociationTenure) =>
    tenure.charAt(0).toUpperCase() + tenure.slice(1);
  const tenureLabel = formatTenure(currentTenure);
  const tenureOptions: PersonAssociationTenure[] = ["permanent", "elite", "casual", "temporary", "affiliate"];

  const deactivate = () => {
    if (state.isDeactivating) return;
    _setState((s) => ({ ...s, isDeactivating: true }));
    PersonApi.deassociatePerson(person._id, associationId)
      .then(() => location.reload())
      .catch((er) => {
        console.error(er);
        toast.error(`Failed to deactivate: ${er?.response?.data?.message || er.message}`);
      })
      .finally(() => _setState((s) => ({ ...s, isDeactivating: false })));
  };

  const updateTenure = (newTenure: PersonAssociationTenure) => {
    if (!activeAssociation || state.isConverting) return;
    _setState((s) => ({ ...s, isConverting: true }));
    PersonApi.associatePerson(person._id, {
      associationId,
      privilege: activeAssociation.privilege,
      role: activeAssociation.role,
      start: new Date(),
      adminPayrate: activeAssociation.adminPayrate,
      tenure: newTenure,
    })
      .then((updatedPerson) => {
        onPersonUpdated(updatedPerson);
        _setState((s) => ({ ...s, extraOptionsIsOpen: false }));
        toast.success(`Converted to ${formatTenure(newTenure)}`);
      })
      .catch((er) => {
        console.error(er);
        toast.error(`Failed to convert to ${newTenure}: ${er?.response?.data?.message || er.message}`);
      })
      .finally(() => _setState((s) => ({ ...s, isConverting: false })));
  };

  return (
    <Table.Tr>
      <Table.Td style={{ width: '100px', minWidth: '100px', wordBreak: 'break-word', fontSize: '14px' }}>
        <PersonLink identifier={person.externalIdentifier} />
      </Table.Td>
      <Table.Td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>{person.firstName}</Table.Td>
      <Table.Td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>{person.lastName}</Table.Td>
      <Table.Td style={{ fontSize: '14px' }}>
        {activeAssociation && activeAssociation.privilege
          ? Privilege.getNames(activeAssociation.privilege).join(", ")
          : ""}
      </Table.Td>
      <Table.Td style={{ minWidth: "100px", fontSize: '14px', verticalAlign: 'top', paddingTop: '12px', paddingBottom: "12px" }}>
        {activeAssociation ? (
          <RoleIconDisplay value={activeAssociation.role} />
        ) : (
          "-"
        )}
      </Table.Td>
      <Table.Td style={{ fontSize: '14px' }}>{tenureLabel}</Table.Td>
      <Table.Td>
        {person.referee && BasketballGrade.toString(person.referee.grade)}
      </Table.Td>
      <Table.Td style={{ width: '80px', minWidth: '80px' }}>
        <Group gap="xs" justify="flex-end">
          <Button.Group>
            <Button
              color="blue"
              variant="light"
              size="xs"
              onClick={() => editOfficial(person._id)}
              style={{
                fontSize: '12px',
                padding: '4px 8px',
                height: '28px'
              }}
            >
              <FontAwesomeIcon icon={faPen} size="xs" />
            </Button>
            <Menu
              opened={state.extraOptionsIsOpen}
              onChange={(opened) => setState({ extraOptionsIsOpen: opened })}
              position="bottom-end"
              withArrow
            >
              <Menu.Target>
                <Button
                  color="blue"
                  variant="light"
                  size="xs"
                  style={{
                    fontSize: '12px',
                    padding: '4px 6px',
                    height: '28px',
                    minWidth: 'auto'
                  }}
                >
                  <FontAwesomeIcon icon={faChevronDown} size="xs" />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {activeAssociation &&
                  tenureOptions
                    .filter((tenure) => tenure !== currentTenure)
                    .map((tenure) => (
                      <Menu.Item
                        key={tenure}
                        disabled={state.isConverting}
                        onClick={() => updateTenure(tenure)}
                      >
                        {`Convert to ${formatTenure(tenure)}`}
                      </Menu.Item>
                    ))}
                <Menu.Item color="red" onClick={deactivate} disabled={state.isDeactivating}>
                  Deactivate
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Button.Group>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

const PrivilegedUsersList: React.FunctionComponent<IPersonsViewerTabProps> = ({
  associationId,
  editOfficial,
  rolesFilter,
}) => {
  if (!associationId) {
    return (
      <p>
        <em>Set an association to manage to see a list of privileged users</em>
      </p>
    );
  }



  const [state, _updateState] = React.useState<IPrivilegedUsersState>({
    isLoading: false,
    personsMap: {},
  });

  const setState = (obj: Partial<IPrivilegedUsersState>) =>
    _updateState({ ...state, ...obj });

  // Load privileged users for the association
  if (!state.isLoading && associationId !== state.currentAssociationId) {
    if (state.privilegedPersons) {
      setState({ isLoading: true });
    }

    // For now, let's filter from the active persons API until we have the specific endpoint
    PersonApi.getActiveAtAssociation(associationId)
      .then((persons) => {
        // Filter persons with manager or shiftSupervisor privileges
        const privilegedPersons = persons.filter(person => {
          const association = person.associations.find(a => a.associationId === associationId);
          if (!association) return false;
          return Privilege.hasPrivilege(association.privilege, Privilege.manager) ||
            Privilege.hasPrivilege(association.privilege, Privilege.shiftSupervisor);
        });

        setState({
          currentAssociationId: associationId,
          personsMap: _.keyBy(privilegedPersons, "_id"),
          privilegedPersons: privilegedPersons.map((p) => p._id),
          filtered: undefined,
          isLoading: false,
        });
      })
      .catch((er) => {
        setState({
          isLoading: false,
          error: er.message,
        });
      });
  }

  const filterPersons = React.useCallback((params: {
    text?: string | null;
    privilege?: Privilege | null;
    roles?: Role[];
  }) => {
    _updateState((prevState) => {
      const textFilter = params.text === undefined ? prevState.filters?.text : params.text;
      const privilegeFilter = params.privilege === undefined ? prevState.filters?.privilege : params.privilege;
      const currentRolesFilter = params.roles === undefined ? prevState.filters?.roles : params.roles;

      let filtered: undefined | string[];

      if (privilegeFilter && privilegeFilter > Privilege.none) {
        filtered = (filtered || prevState.privilegedPersons || []).filter((pid) => {
          const pPrivilege = prevState.personsMap[pid]?.associations.filter(
            (a) => a.associationId === associationId,
          )[0]?.privilege;
          if (pPrivilege === undefined) return false;
          return Privilege.hasPrivilege(pPrivilege, privilegeFilter);
        });
      }

      if (currentRolesFilter && currentRolesFilter.length > 0) {
        filtered = (filtered || prevState.privilegedPersons || []).filter((pid) => {
          const pRole = prevState.personsMap[pid]?.associations.filter(
            (a) => a.associationId === associationId,
          )[0]?.role;
          if (pRole === undefined) return false;
          return currentRolesFilter.some(role => Role.hasRole(pRole, role));
        });
      }

      if (textFilter) {
        const kwds = textFilter.toLowerCase().split(" ");
        if (kwds.length > 0) {
          filtered = (filtered || prevState.privilegedPersons || []).filter((pid) =>
            SearchHelper(prevState.personsMap[pid], kwds),
          );
        }
      }

      return {
        ...prevState,
        filtered,
        filters: {
          text: textFilter || undefined,
          privilege: privilegeFilter || undefined,
          roles: currentRolesFilter || undefined,
        },
      };
    });
  }, [associationId]);

  React.useEffect(() => {
    filterPersons({ roles: rolesFilter });
  }, [rolesFilter, filterPersons]);

  const visibleIds = state.filtered || state.privilegedPersons || [];

  // Create a simple update handler for the shared table
  const handlePersonUpdate = React.useCallback((updatedPerson: IPerson) => {
    if (!updatedPerson?._id) return;
    _updateState((prevState) => ({
      ...prevState,
      personsMap: {
        ...prevState.personsMap,
        [updatedPerson._id]: updatedPerson,
      },
    }));
  }, []);

  return (
    <div>
      <Alert color="yellow">
        <strong>Note:</strong> Showing users with elevated privileges
      </Alert>
      <div style={{ width: "100%", marginBottom: "10px" }}>
        <TextInput
          className="mr-sm-4"
          placeholder="Search users with elevated privileges"
          onChange={(e) => filterPersons({ text: e.currentTarget.value })}
          style={{
            maxWidth: "100%",
            minWidth: "200px",
            width: "100%",
            marginBottom: "10px",
          }}
        />
        <Text
          c="dimmed"
          size="sm"
          ta="right"
          style={{ marginTop: "8px" }}
        >
          <em>{visibleIds.length} users with elevated privileges</em>
        </Text>
      </div>
      <PersonsTable
        personIds={visibleIds}
        personsMap={state.personsMap}
        associationId={associationId}
        editOfficial={editOfficial}
        onPersonUpdated={handlePersonUpdate}
        isLoading={state.isLoading}
      />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonsViewer);
